import {
  AddCircleOutline,
  Badge,
  GroupTreeNode,
  Accordion,
  AccordionItem,
  PridePeopleCount,
} from '@aslanpay/portal-common-lib';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { GroupWithPot } from 'app/hooks/useGroupsWithPot/types';

import { companyPaths } from 'app/constants/routes';
import { useManagingCompanyStore } from 'app/store';

import { PridesContentProps } from './PridesContent.types';

function mapTreeToAccordionItems(
  nodes: GroupWithPot[],
  renderRightBadge?: (node: GroupWithPot) => React.ReactNode,
  renderLeftBadge?: (node: GroupWithPot) => React.ReactNode
): AccordionItem[] {
  return nodes.map((node) => ({
    id: node.id,
    label: node.name,
    leftBadge: renderLeftBadge?.(node),
    rightBadge: renderRightBadge?.(node),
    defaultOpen: !node.parentGroupId,
    ...(node.children?.length
      ? {
          children: mapTreeToAccordionItems(node.children, renderRightBadge, renderLeftBadge),
        }
      : {}),
  }));
}

function PridesContent({ groups, setSelectedGroup, onCreatePot }: PridesContentProps) {
  const navigate = useNavigate();
  const companyId = useManagingCompanyStore((state) => state.company?.id) ?? '';

  const renderPotInformation = useCallback(
    (group: GroupWithPot) => {
      if (group?.pot && group?.pot?.id) {
        return (
          <Flex direction="row" alignItems="center" gap="5px">
            <Flex direction="row" alignItems="center" gap="8px">
              <Text
                textStyle="smallMedium"
                color="brand.darkGray"
              >{`${group.pot.balance} pts`}</Text>

              <Text as="h5" textStyle="h5Bold" color="brand.mediumGray">
                •
              </Text>
              <Text
                textStyle="smallMedium"
                color="brand.darkGray"
              >{`${group.managerCount} pot managers`}</Text>
            </Flex>

            <Button
              size="md"
              variant="ghost"
              onClick={() => {
                navigate(
                  generatePath(companyPaths.pot.path, {
                    companyId,
                    potId: group.pot?.id || '',
                  })
                );
              }}
            >
              View Pot
            </Button>
          </Flex>
        );
      }

      return (
        <Button
          size="md"
          variant="ghost"
          leftIcon={<AddCircleOutline boxSize="20px" />}
          onClick={() => onCreatePot(group)}
        >
          Create Pot
        </Button>
      );
    },
    [navigate, onCreatePot, companyId]
  );

  const items = useMemo<AccordionItem[]>(() => {
    return mapTreeToAccordionItems(
      groups,
      (node: GroupWithPot) => {
        const totalMembers = node.memberCount + node.managerCount;
        return (
          <Flex direction="row" alignItems="center" gap="5px">
            {renderPotInformation(node)}

            <PridePeopleCount total={totalMembers} onClick={() => setSelectedGroup(node)} />
          </Flex>
        );
      },
      (node: GroupTreeNode) => (!node.parentGroupId ? <Badge value="Root" /> : undefined)
    );
  }, [groups, setSelectedGroup, renderPotInformation]);

  return (
    <Box borderRadius="10px" border="1px solid" borderColor="brand.lightGray1" p="20px">
      <Accordion items={items} />
    </Box>
  );
}

export default PridesContent;
